import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import { Badge } from '../primitives/Display.js';
import { Tooltip } from '../primitives/Tooltip.js';
import './shell.css';

/* ---------- AppShell ---------- */

export interface AppShellProps {
  nav: ReactNode;
  topBar: ReactNode;
  /** Sticky patient context bar under the top bar when a patient is open. */
  contextBar?: ReactNode;
  children: ReactNode;
  /** Mirrors SideNav's collapsed state so the grid column matches. */
  navCollapsed?: boolean;
  /** id of the main region, target of the skip link. */
  mainId?: string;
  className?: string;
}

/** Inverted-L shell (Linear): nav left, top bar, content (BUILD-PLAN 3.4). */
export function AppShell({
  nav,
  topBar,
  contextBar,
  children,
  navCollapsed = false,
  mainId = 'main',
  className,
}: AppShellProps) {
  return (
    <div className={cx('vel-shell', className)} data-nav-collapsed={navCollapsed ? '' : undefined}>
      <a className="vel-skip" href={`#${mainId}`}>
        Skip to content
      </a>
      <div className="vel-shell__nav">{nav}</div>
      <div className="vel-shell__top">{topBar}</div>
      <div className="vel-shell__body">
        {contextBar ? <div className="vel-shell__context">{contextBar}</div> : null}
        <main id={mainId} className="vel-shell__main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

/* ---------- SideNav ---------- */

export interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
  href: string;
  /** Unread or work-queue count; 0 hides it. */
  badge?: number;
  /** Badge tone for counts that need attention now. */
  badgeTone?: 'neutral' | 'danger';
  /** Locked Marketplace module: shown with a lock hint, still navigable to its upsell panel. */
  locked?: boolean;
}

export interface NavGroup {
  id: string;
  /** Omit for the ungrouped top section (Dashboard, Schedule). */
  label?: string;
  items: readonly NavItem[];
}

export interface NavLinkRenderProps {
  href: string;
  className: string;
  'aria-current': 'page' | undefined;
  children: ReactNode;
}

export interface SideNavProps {
  groups: readonly NavGroup[];
  activeId?: string;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Brand slot at the top (BrandMark). */
  header?: ReactNode;
  /** Bottom slot (help, release notes). */
  footer?: ReactNode;
  /** Router integration: render a framework link. Defaults to <a>. */
  renderLink?: (props: NavLinkRenderProps) => ReactNode;
  className?: string;
}

const defaultLink = ({ children, ...props }: NavLinkRenderProps) => <a {...props}>{children}</a>;

/** Grouped, collapsible nav, 240/56 px, active indicator in the brand gradient. */
export function SideNav({
  groups,
  activeId,
  collapsed = false,
  onCollapsedChange,
  header,
  footer,
  renderLink = defaultLink,
  className,
}: SideNavProps) {
  return (
    <nav
      className={cx('vel-nav', className)}
      data-collapsed={collapsed ? '' : undefined}
      aria-label="Main"
    >
      {header ? <div className="vel-nav__header">{header}</div> : null}
      <div className="vel-nav__scroll">
        {groups.map((g) => (
          <div key={g.id} className="vel-nav__group">
            {g.label ? (
              collapsed ? (
                <div className="vel-nav__group-sep" role="separator" aria-label={g.label} />
              ) : (
                <div className="vel-nav__group-label">{g.label}</div>
              )
            ) : null}
            <ul className="vel-nav__list">
              {g.items.map((item) => {
                const active = item.id === activeId;
                const link = renderLink({
                  href: item.href,
                  className: 'vel-nav__item',
                  'aria-current': active ? 'page' : undefined,
                  children: (
                    <>
                      <span className="vel-nav__icon" aria-hidden>
                        {item.icon}
                      </span>
                      <span className="vel-nav__label">{item.label}</span>
                      {item.badge ? (
                        <Badge
                          tone={item.badgeTone === 'danger' ? 'danger' : 'neutral'}
                          variant={item.badgeTone === 'danger' ? 'solid' : 'soft'}
                          className="vel-nav__badge"
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </Badge>
                      ) : null}
                      {item.locked && !collapsed ? (
                        <span className="vel-nav__locked">Add-on</span>
                      ) : null}
                    </>
                  ),
                });
                return (
                  <li key={item.id}>
                    {collapsed ? (
                      <Tooltip
                        side="right"
                        content={item.badge ? `${item.label} (${item.badge})` : item.label}
                      >
                        <span className="vel-nav__tip-anchor">{link}</span>
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="vel-nav__footer">
        {footer}
        {onCollapsedChange ? (
          <button
            type="button"
            className="vel-nav__item vel-nav__collapse"
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
          >
            <span className="vel-nav__icon" aria-hidden>
              {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </span>
            <span className="vel-nav__label">Collapse</span>
          </button>
        ) : null}
      </div>
    </nav>
  );
}
